import { TBulkImportLearnerItemDto, TBulkImportLearnerReqDto } from "@/api/services/main/learner/dtos/bulk-import-learner.dto";
import { TBulkImportRow } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";

// 日付文字列 (YYYY/MM/DD) を BE が要求する ISO (YYYY-MM-DD) へ。空はそのまま空で返す。
const toIsoDate = (value: string): string => (value ? value.replace(/\//g, "-") : "");

// 取り込み確定保存に必須の項目の単一定義。判定(isComplete)・赤枠(columnId)・エラーメッセージ(label)を
// ここから導出して、件数と「どの項目が未入力か」が絶対に食い違わないようにする。
// FE 必須: nationalityId / industryId / gender / learnerPlan(learningStartDate, examScheduledYearMonth)。
// 1号の起算日(clause1StartDate) は表に編集列が無く visa 由来のため空でも取り込み可とする
// (= 1号残期間が空でもブロックしない)。BE 側の NOT NULL 制約とは別管理。
type TRequiredField = {
  isEmpty: (r: TBulkImportRow) => boolean;
  // 赤枠表示するテーブルのカラムID (= 編集可能セル)。列が無い項目は省略。
  columnId?: string;
  // エラーメッセージで表示する項目名。
  label: string;
};

// 前回点数の上限。create / edit learner と共通の product rule (0〜1000 の整数。learnerSchema の MAX_EXAM_SCORE と一致)。
export const MAX_LAST_SCORE = 1000;

// 前回点数の入力値が不正か。入力済みかつ「0〜1000 の整数」でない場合に true。
// 空欄は任意項目なので不正としない (= 取り込みをブロックしない)。
// セル単位の即時バリデーション(blur時の赤枠)でも使うため、行ではなく値を受け取る。
export const isLastScoreValueInvalid = (value: string): boolean => {
  if (!value) return false;
  const score = Number(value);
  return !Number.isInteger(score) || score < 0 || score > MAX_LAST_SCORE;
};

// 前回点数(lastScore)が不正な行か。
export const isLastScoreInvalid = (row: TBulkImportRow): boolean => isLastScoreValueInvalid(row.lastScore);

const REQUIRED_FIELDS: TRequiredField[] = [
  { isEmpty: (r) => r.industryId == null, columnId: "industryId", label: "受験分野" },
  { isEmpty: (r) => r.nationalityId == null, columnId: "nationality", label: "国籍" },
  { isEmpty: (r) => r.genderValue == null, columnId: "gender", label: "性別" },
  // カリキュラム開始日 / 受験予定 / 受験予定年月 は学習アプリ ON の行のみ必須。
  // OFF の行はこれらをクリア&無効化するため、必須対象から外す (= 空でも取り込み可。手動フォームと同じ扱い)。
  { isEmpty: (r) => r.learningApp && !r.curriculumStartDate, columnId: "curriculumStartDate", label: "カリキュラム開始日" },
  { isEmpty: (r) => r.learningApp && r.examId == null, columnId: "examId", label: "受験予定" },
  { isEmpty: (r) => r.learningApp && !r.examScheduleYm, columnId: "examScheduleYm", label: "受験予定年月" },
];

// 取り込み確定保存に必須の項目が全て埋まっているか。空欄があれば false。
export const isBulkImportRowComplete = (row: TBulkImportRow): boolean => REQUIRED_FIELDS.every((f) => !f.isEmpty(row));

// 空欄の必須項目を持つ行数 (トースト「XX件の空欄の必須項目」用)。
export const countIncompleteBulkImportRows = (rows: TBulkImportRow[]): number => rows.filter((row) => !isBulkImportRowComplete(row)).length;

// 前回点数が不正な行数 (トースト「XX件の前回点数」用)。
export const countInvalidLastScoreRows = (rows: TBulkImportRow[]): number => rows.filter((row) => isLastScoreInvalid(row)).length;

// 1行の中で赤枠表示するカラムID一覧。空欄の必須項目 (列を持たない項目は除く) + 範囲超過の前回点数。
export const getRowErrorColumns = (row: TBulkImportRow): string[] => {
  const cols = REQUIRED_FIELDS.filter((f) => f.isEmpty(row) && f.columnId).map((f) => f.columnId as string);
  // 前回点数の範囲超過は必須ではないが取り込みをブロックするため赤枠対象にする。
  if (isLastScoreInvalid(row)) cols.push("lastScore");
  return cols;
};

// エラーのある行の { rowId: [カラムID...] } マップ。エラーが無い行は含めない。
export const buildBulkImportErrorMap = (rows: TBulkImportRow[]): Record<number, string[]> => {
  const map: Record<number, string[]> = {};
  for (const row of rows) {
    const cols = getRowErrorColumns(row);
    if (cols.length > 0) map[row.id] = cols;
  }
  return map;
};

// 最初にエラーのある行ID (必須未入力 or 前回点数不正)。スクロール/フォーカス先。無ければ null。
export const getFirstErrorRowId = (rows: TBulkImportRow[]): number | null =>
  rows.find((row) => !isBulkImportRowComplete(row) || isLastScoreInvalid(row))?.id ?? null;

// 不備行ごとの「X行目：氏名 - 〇〇、△△ が未入力です。」メッセージ一覧 (クリックで該当行へスクロールできるよう rowId 付き)。
// REQUIRED_FIELDS から直接導出するので、列が無い項目(1号の起算日)も漏れなく表示される
// = 件数と表示項目が食い違わない。
export type TBulkImportErrorMessage = { rowId: number; text: string };

export const buildBulkImportErrorMessages = (rows: TBulkImportRow[]): TBulkImportErrorMessage[] => {
  const messages: TBulkImportErrorMessage[] = [];
  rows.forEach((row, index) => {
    const fields = REQUIRED_FIELDS.filter((f) => f.isEmpty(row)).map((f) => f.label);
    if (fields.length === 0) return;
    const name = row.fullName ? `${row.fullName} - ` : "";
    messages.push({ rowId: row.id, text: `${index + 1}行目：${name}${fields.join("、")} が未入力です。` });
  });
  return messages;
};

// 行の industryId から「受験・学習対象のパート」既定値を解決する関数。
// 取り込みは複数分野が混在するため、行ごとに分野の partTypes を解決する必要がある (共通値は使えない)。
export type TResolvePartTypes = (row: TBulkImportRow) => string[] | null;

// 居住地(visa の都道府県/市区町村)を study マスタの正規名へ寄せる関数。手動作成フォームと同じ値で保存する。
// 未指定時は visa の生値をそのまま使う。
export type TResolveResidence = (row: TBulkImportRow) => { prefecture: string | null; city: string | null };

// 編集行 → 保存 API ペイロード。所属機関は新規(newCompanyName)/既存(rsoCompanyId)のどちらかに振り分ける。
const toBulkImportItem = (
  row: TBulkImportRow,
  resolvePartTypes?: TResolvePartTypes,
  resolveResidence?: TResolveResidence,
): TBulkImportLearnerItemDto => ({
  // 所属機関は rsoCompanyId が有れば既存に紐付け、無ければ (「(新規)」選択) BE 側で新規作成する。
  rsoCompanyId: row.isNewCompany ? null : (row.rsoCompanyId ?? null),
  newCompanyName: row.isNewCompany ? row.companyName : null,
  // visa 所属機関情報は新規/既存問わず渡す。新規は作成データに、既存は空欄の担当者情報等の補完に使う。
  company: row.company ?? null,
  nationalityId: row.nationalityId as number,
  industryId: row.industryId as number,
  // visa 由来の行のみ従業員IDを送る (BE が learner に保存し既存登録検出に使う)。
  // ファイル取り込みの行は visa 従業員IDを持たないため null (行番号の id を誤って送らない)。
  visaEmployeeId: row.visaEmployeeId ?? null,
  fullName: row.fullName,
  nickName: row.nickName ?? null,
  gender: row.genderValue as number,
  // 居住地: visa 由来の 都道府県/市区町村 を study マスタの正規名へ寄せて保存 (resolver 未指定は生値。空は null)。
  prefecture: (resolveResidence ? resolveResidence(row).prefecture : row.province) || null,
  city: (resolveResidence ? resolveResidence(row).city : row.municipality) || null,
  // 未入力は null で送る (空文字は不正な日付になるため)。
  clause1StartDate: row.clause1StartDate ? toIsoDate(row.clause1StartDate) : null,
  blankPeriodMonths: row.blankPeriodMonths ?? null,
  japaneseLanguageLevel: row.japaneseLevel || null,
  // 前回試験の受験日はファイル取り込みでのみ入力される (visa 同期の行は値を持たない)。
  // BE は未知プロパティを拒否する (ValidatePipe: forbidNonWhitelisted) ため、値がある行だけキーを付ける。
  // これにより visa 同期の送信内容は従来と完全に同一になり、BE の対応状況に影響されない。
  ...(row.lastExamDate ? { lastExamDate: toIsoDate(row.lastExamDate) } : {}),
  lastExamScore: row.lastScore ? Number(row.lastScore) : null,
  // 手続き管理 / 学習アプリ のトグル。未送信だと BE 側 default=true になり OFF が反映されないため明示的に送る。
  isProcedureManaged: row.procedureManagement,
  isUsingLearningApp: row.learningApp,
  learnerPlan: {
    planStatus: row.status || null,
    // カリキュラム開始日 / 受験予定年月 は学習アプリ OFF の行では空になる (必須対象外)。
    // 空文字を送ると BE 側で日付/年月バリデーションに弾かれ保存できないため、手動フォーム(convertLearnerFormData)と
    // 同じく未入力は null で送る。
    learningStartDate: row.curriculumStartDate ? toIsoDate(row.curriculumStartDate) : null,
    examScheduledYearMonth: row.examScheduleYm || null,
    // 受験予定(分野別試験一覧の試験ID)。BE は learnerPlan.examId をそのまま保存する。
    examId: row.examId ?? null,
    // 受験・学習対象のパート。手動作成(CreateLearnerPlanModal)と同じく分野の全パートを既定で ON にする。
    // 学習アプリ OFF の行はフォーム挙動に合わせ null (パート選択は学習アプリ利用時のみ意味を持つ)。
    partTypes: row.learningApp ? (resolvePartTypes?.(row) ?? null) : null,
  },
});

export const buildBulkImportPayload = (
  rows: TBulkImportRow[],
  resolvePartTypes?: TResolvePartTypes,
  resolveResidence?: TResolveResidence,
): TBulkImportLearnerReqDto => ({
  learners: rows.map((row) => toBulkImportItem(row, resolvePartTypes, resolveResidence)),
});
